import React, {useEffect, useRef} from 'react';

const ALLOWED_TAGS = new Set(['A', 'B', 'STRONG', 'I', 'EM', 'U', 'BR', 'P', 'DIV', 'SPAN', 'UL', 'OL', 'LI',
    'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'TABLE', 'THEAD', 'TBODY', 'TFOOT', 'TR', 'TH', 'TD', 'CAPTION',
    'COLGROUP', 'COL', 'BLOCKQUOTE', 'PRE', 'CODE']);
const DROP_WITH_CONTENT = new Set(['SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'META', 'LINK', 'TITLE', 'HEAD']);
const ALLOWED_ATTRS = {A: ['href'], TD: ['colspan', 'rowspan'], TH: ['colspan', 'rowspan', 'scope']};

// Allow-list sanitiser for pasted / stored markup: keeps structure (tables, lists, basic formatting) and
// drops scripts, event handlers, inline styles and unknown tags (their text is kept).
export function sanitizeHtml(html) {
    const doc = new DOMParser().parseFromString(String(html || ''), 'text/html');
    const clean = (node) => {
        Array.from(node.childNodes).forEach((child) => {
            if (child.nodeType === Node.COMMENT_NODE) return child.remove();
            if (child.nodeType !== Node.ELEMENT_NODE) return;
            if (DROP_WITH_CONTENT.has(child.tagName)) return child.remove();
            clean(child);
            if (!ALLOWED_TAGS.has(child.tagName)) return child.replaceWith(...Array.from(child.childNodes));
            const keep = ALLOWED_ATTRS[child.tagName] || [];
            Array.from(child.attributes).forEach((attr) => {
                const unsafeHref = attr.name === 'href' && !/^(https?:|mailto:)/i.test(attr.value.trim());
                if (!keep.includes(attr.name) || unsafeHref) child.removeAttribute(attr.name);
            });
            if (child.tagName === 'A') { child.setAttribute('target', '_blank'); child.setAttribute('rel', 'noopener noreferrer'); }
        });
    };
    clean(doc.body);
    return doc.body.innerHTML;
}

export const htmlToText = (html) => {
    const el = document.createElement('div');
    el.innerHTML = sanitizeHtml(html);
    return (el.textContent || '').trim();
};

// Contenteditable box without a toolbar: typed text and pasted HTML (e.g. a table) are kept and shown as
// markup; the value is the sanitised HTML string.
export default function RichTextArea({id, className, value, placeholder, onChange}) {
    const ref = useRef(null);

    useEffect(() => {
        const el = ref.current;
        if (el && el.innerHTML !== value) el.innerHTML = sanitizeHtml(value);
    }, [value]);

    const emit = () => {
        const el = ref.current;
        onChange(htmlToText(el.innerHTML) || el.querySelector('table, img') ? sanitizeHtml(el.innerHTML) : '');
    };

    const handlePaste = (e) => {
        const html = e.clipboardData.getData('text/html');
        if (!html) return; // plain text: let the browser insert it
        e.preventDefault();
        document.execCommand('insertHTML', false, sanitizeHtml(html));
        emit();
    };

    return (
        <div id={id} ref={ref} className={`${className} di-richtext`} contentEditable suppressContentEditableWarning
             role="textbox" aria-multiline="true" data-placeholder={placeholder}
             onInput={emit} onPaste={handlePaste} onBlur={emit}/>
    );
}
